import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  CancelOutlined,
  MoreHoriz,
} from "@mui/icons-material";
import {
  apiCallRatingListData,
  apiCallRatingAdd,
  apiCallStatusUpdate,
  apiCallRatingUpdate
} from "../../../../Core/Apicall";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../Redux/Store";
import { clearShowMenu } from '../../../../Redux/Reducers/GlobalVar'
import DeletePopup from "../../../Common/DeletePopup/DeletePopup";
import CommonMenu from "../../../Common/CommonMenu/CommonMenu";
import CommonStatus from "../../../Common/CommonStatus/CommonStatus";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";
import { toast } from 'react-toastify';

interface ratingType {
  _id?: string;
  question: string;
  displayOrder: number | string;
  category: string;
  isActive: boolean;
  isDelete: boolean;
}

function Rating() {
  const [data, setData] = useState<ratingType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [search, setSearch] = useState<string>("");
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)

  const showMenu: any = useSelector((state: RootState) => state.global.showMenu);
  const userdata: any = useSelector((state: RootState) => state.global.token);

  const [isCreateNew, setIsCreateNew] = useState<boolean>(!!showMenu);
  useEffect(() => {
    setIsCreateNew(!!showMenu);
  }, [showMenu]);

  const [formData, setFormData] = useState<any>({
    questionId: "",
    question: "",
    displayOrder: "",
    category: "",
    isActive: true,
    isDelete: false,
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuVibe, setMenuVibe] = useState<ratingType | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);


  // 📌 Fetch rating list
  useEffect(() => {
    const fetchRatings = async () => {
      setLoading(true);
      try {
        const response = await apiCallRatingListData("");
        if (Array.isArray(response?.data)) {
          setData(response.data);
        } else {
          setData([]);
        }
      } catch (err: any) {
  
      } finally {
        setLoading(false);
      }
    };
    fetchRatings();
  }, []);

  // 📌 Input change
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  // 📌 Submit form
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {

      if (formData.questionId) {
        // iconPath: "",
        const data = {
          adminId: userdata?.data?.userId,
          questionId: formData.questionId,
          question: formData.question,
          category: formData.category,
          displayOrder: Number(formData.displayOrder),
        }
        await apiCallRatingUpdate(data);
        toast.success("Rating updated successfully!")
      } else {
        await apiCallRatingAdd(formData);
        toast.success("Rating saved successfully!");
      }
      setIsCreateNew(false);
      setFormData({
        questionId: "",
        question: "",
        displayOrder: "",
        category: "",
        isActive: true,
        isDelete: false,
      });

      const response = await apiCallRatingListData("");
      setData(response?.data || []);
    } catch (err) {
      toast.error("Failed to save Rating");
    }
  };

  // 📌 Toggle status
  const handleStatusUpdate = async (item: any) => {
    try {
      await apiCallStatusUpdate({
        adminId: userdata?.data?.userId,
        typeId: item._id,
        type: "star_rating",
        isActive: !item.isActive,
      });
      setData((prev) =>
        prev.map((m) =>
          m._id === item._id
            ? { ...m, isActive: !item.isActive }
            : m
        )
      );
      setStatusDialogOpen(false)
    } catch (err) {
    }
  };
  const handleDelete = async () => {
    if (!menuVibe) return
    try {
      await apiCallStatusUpdate({
        adminId: userdata?.data?.userId,
        type: "star_rating",
        typeId: menuVibe._id,
        isDelete: true
      })
      setData(prev => prev.filter(item => item._id !== menuVibe._id))
    } catch (err) {
    } finally {
      setDeleteDialogOpen(false)
      setMenuVibe(null)
    }
  }

  // ------------------- Menu Handlers -------------------
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, type: any) => {
    setAnchorEl(event.currentTarget)
    setMenuVibe(type)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const menuItems = [
    {
      label: "Edit",
      onClick: () => {
        if (menuVibe) {
          setFormData({
            ...menuVibe,
            questionId: menuVibe._id,
          });
          setIsCreateNew(true);
        }
        handleMenuClose();
      },
    },
    {
      label: "Delete",
      onClick: () => setDeleteDialogOpen(true),
    }

  ];

  return (
    <Box style={{ paddingBottom: "20px" }} >
      <Box className="d-flex input align-items-center justify-content-between">
        <TextField
          className="border-10"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ backgroundColor: "white" }}
          placeholder="Search Questions"
        />
        <Button
          className="btn-black p-2 px-3"
          onClick={() => {
            setFormData({
              questionId: "",
              question: "",
              displayOrder: "",
              category: "",
              isActive: true,
              isDelete: false,
            });
            setIsCreateNew(true);
          }}
        >
          + Create New
        </Button>
      </Box>

      <Paper
        className="bg-white mt-3 border-20 scroll-bar"
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <TableContainer
          className="scroll-bar"
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Display Order</TableCell>
                <TableCell>Question</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data
                .filter(
                  (item) =>
                    search === "" ||
                    item.question
                      .toLowerCase()
                      .includes(search.toLowerCase())
                )
                .map((item, index: any) => (
                  <TableRow hover key={index}>
                    <TableCell>{item.displayOrder}</TableCell>
                    <TableCell>{item.question}</TableCell>
                    <TableCell>{item.category}</TableCell>
                    <TableCell>
                      <Button
                        className={item?.isActive ? 'btn-success' : 'btn-danger'}
                        onClick={() => setStatusDialogOpen(index)}
                      >
                        {item?.isActive ? "Active" : "Inactive"}
                      </Button>
                      <CommonStatus
                        open={statusDialogOpen === index}
                        title={item?.question}
                        message={`Are you sure you want to ${item?.isActive ? "deactivate" : "activate"} this venue?`}
                        confirmText={item?.isActive ? "Deactivate" : "Activate"}
                        confirmColor={item?.isActive ? "warning" : "success"}
                        onConfirm={() => handleStatusUpdate(item)}
                        onClose={() => setStatusDialogOpen(false)}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={(e) => handleMenuOpen(e, item)}>
                        <MoreHoriz />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {loading &&
            <div style={{ padding: "10px 10px" }}>
              <CommonShimmer />
            </div>}
        </TableContainer>
      </Paper>

      <CommonMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        items={menuItems}
      />

      <DeletePopup
        open={deleteDialogOpen}
        title="Confirm Delete"
        message="Are you sure you want to delete this venue?"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onClose={() => setDeleteDialogOpen(false)}
      />

      <Drawer
        anchor="right"
        open={isCreateNew}
        onClose={() => {
          setIsCreateNew(false)
          dispatch(clearShowMenu());
        }}
        PaperProps={{
          sx: { width: "35%", borderRadius: "16px 0 0 16px", overflow: "hidden" },
        }}
      >
        <Box
          className="Rating-form"
          height="100%"
          display="flex"
          flexDirection="column"
        >
          {/* ---------- Header ---------- */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={3}
            py={2}
            borderBottom="1px solid #eee"
            bgcolor="#fafafa"
          >
            <Typography variant="h6" fontWeight={600}>
              {formData.ratingId ? "Edit Rating" : "Create Rating"}
            </Typography>
            <IconButton onClick={() => {
              setIsCreateNew(false)
              dispatch(clearShowMenu());
            }
            }>
              <CancelOutlined />
            </IconButton>
          </Box>

          {/* ---------- Form ---------- */}
          <Box flex={1} px={3} py={2} overflow="auto">
            <form onSubmit={handleSubmit}>
              {/* Question */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Question
                </Typography>
                <TextField
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  placeholder="Enter Rating Question"
                  fullWidth
                />
              </FormControl>

              {/* Display Order */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Display Order
                </Typography>
                <TextField
                  name="displayOrder"
                  type="number"
                  value={formData.displayOrder}
                  onChange={handleChange}
                  placeholder="Enter Display Order"
                  fullWidth
                />
              </FormControl>

              {/* Category */}
              <FormControl fullWidth sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} mb={1}>
                  Category
                </Typography>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData((prev: any) => ({ ...prev, category: e.target.value }))
                  }
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Select Category</em>
                  </MenuItem>
                  <MenuItem value="Cleanliness">Cleanliness</MenuItem>
                  <MenuItem value="CheckIn">CheckIn</MenuItem>
                  <MenuItem value="Billing">Billing</MenuItem>
                </Select>
              </FormControl>

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  borderRadius: "10px",
                  py: 1.2,
                  fontWeight: 600,
                  textTransform: "none",
                  backgroundColor: "#000",
                  "&:hover": { backgroundColor: "#222" },
                }}
              >
                Save
              </Button>
            </form>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}

export default Rating;
